import { Box, Button, Grid, Table, TableBody, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { useLoading } from "../../../../providers/loadingProvider";
import {
  institutionApplicantsSelector,
  institutionSelector,
  programsSelector,
} from "../../../../store/researchauthoritiesSlice";
import { GifColors } from "../../../../theme";
import StyledTableCell from "../../Components/Tables/StyledTableCell";
import StyledTableRow from "../../Components/Tables/StyledTableRow";
import {
  applicantEligibilityStatus,
  proposalEligibilityStatus,
} from "../../Constants";

const applicantsDetailsOverview = 
`Dear colleagues,

Welcome to your GIF portal. Within this platform, you can stay updated about researchers from your institution who are currently working on their applications to GIF.
Please find below a few key points:
•	You can view only researchers affiliated with your institution.
•	Your main and most important task is to approve OR decline a researcher from submitting an application. 
•	Please note that until you approve a researcher, they will not be able to complete the submission of their application. 
•	Please check this portal daily during the submission period. 
•	In case that there are multiple Israelis PI's on the same application, all relevant research authorities will be notified. However, the authority to which the applicant leader is affiliated with will be responsible to approve and submit the application to GIF.
•	You have the option to write notes. These notes are for your convenience and are for the RA internal use ONLY.

If you have any questions please do not hesitate to contact us at: gif.admin@gif.org.il

Warm regards,
GIF TEAM`;

const useStyles = makeStyles((theme) => ({
  infoIcon: {
    marginLeft: 5,
  },
  paper: {
    marginBottom: 10,
  },
  table: {
    minWidth: 650,
    marginLeft: 5,
    marginRight: 5,
  },
  totalRow: {
    backgroundColor: "#D0D0D0",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  button: {
    "&:hover": {
      backgroundColor: theme.palette.gif.yellow.hover,
    },
    color: theme.palette.gif.yellow.dark,
    backgroundColor: theme.palette.gif.yellow.color,
  },
  tableHeader: {
    marginLeft: 5,
    marginTop: 10,
    padding: 3,
    fontSize: 18,
    fontWeight: 600,
    color: GifColors.israelBlue,
  },
}));

const countUniqueProposals = (proposals) => {
  var uniqueProposals = [];
  proposals.forEach((proposal) => {
    if (!uniqueProposals.some((p) => p.proposalId === proposal.proposalId))
      uniqueProposals.push(proposal);
  });

  return uniqueProposals.length;
};

const buildProgram = (applicants, programType) => {
  const programApplicants = applicants.filter(
    (a) => a.programTypeNumber === programType.number
  );
  const waiting = programApplicants.filter(
    (a) =>
      a.applicantStatus === null ||
      a.applicantStatus === applicantEligibilityStatus.waitingForApproval ||
      a.applicantStatus === applicantEligibilityStatus.ChangeDecision
  );
  const approved = programApplicants.filter(
    (a) => a.applicantStatus === applicantEligibilityStatus.approvedByRA
  );
  const proposalsWating = programApplicants.filter(
    (a) =>
      a.proposalStatus ===
      proposalEligibilityStatus.submittedByApplicationLeader
  );
  const proposalsUnlock = programApplicants.filter(
    (a) => a.proposalStatus === proposalEligibilityStatus.unlockedByRA
  );
  const proposalsWatingNunber = countUniqueProposals(proposalsWating);
  const proposalsUnlockNunber = countUniqueProposals(proposalsUnlock);
  return {
    name: programType.name,
    totalApplicants: waiting.length + approved.length + proposalsWatingNunber + proposalsUnlockNunber,
    tableRows: [
      {
        label: "Researchers waiting for your approval",
        value: waiting.length,
        path: `/researchauthorities/applicants/researchers?tab=0&program=${programType.number}`,
      },
      {
        label: "Approved researchers",
        value: approved.length,
        path: `/researchauthorities/applicants/researchers?tab=1&program=${programType.number}`,
      },
      {
        label: "Applications waiting for approval",
        value: proposalsWatingNunber,
        path: `/researchauthorities/applicants/applications?tab=0&program=${programType.number}`,
      },
      {
        label: "Unlocked Applications",
        value: proposalsUnlockNunber,
        path: `/researchauthorities/applicants/applications?tab=1&program=${programType.number}`,
      },
    ],
  };
};

export default function Overview() {
  const classes = useStyles();
  const history = useHistory();
  const loading = useLoading();
  const institutionApplicants = useSelector(institutionApplicantsSelector);
  const institution = useSelector(institutionSelector);
  const programsTypes = useSelector(programsSelector);
  const [firstLoading, setFirstLoading] = useState(null);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    var loadingId = null;
    if (!firstLoading) {
      loadingId = loading.addLoading("Loading Researchers & Applications");
      setFirstLoading(loadingId);
    }
    if (institutionApplicants === null || programsTypes === null) return;
    const programs = [];
    programsTypes.forEach((programType) => {
      const program = buildProgram(institutionApplicants, programType);
      programs.push(program);
    });
    setPrograms(programs);

    if (loadingId) loading.removeLoading(loadingId);
    else loading.removeLoading(firstLoading);
  }, [institutionApplicants, loading, firstLoading, institution, programsTypes]);

  return (
    <Grid container >
      <Grid item xs={12}>
        <Box className={classes.header}>
          <Typography variant="subtitle2">Applicants Overview</Typography>
          <InfoOutlinedIcon className={classes.infoIcon} color="primary" />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <pre style={{ fontFamily: "inherit", fontSize: 14,whiteSpace:'break-spaces'}}>
          {applicantsDetailsOverview}
        </pre>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle2">{institution?.name} summary</Typography>
      </Grid>
      <Grid item xs={12}>
        {programs.map((program) => (
          program.totalApplicants > 0 && 
          <Box marginTop={5} key={program.name}>
            <Typography className={classes.tableHeader}>
              {program.name}
            </Typography>
            <Table className={classes.table}>
              <TableBody>
                {program.tableRows.map((row) => (
                  <StyledTableRow key={row.label}>
                    <StyledTableCell>{row.label}</StyledTableCell>
                    <StyledTableCell align="right">{row.value}</StyledTableCell>
                    <StyledTableCell align="right">
                      <Button
                        className={classes.button}
                        size="small"
                        onClick={() => history.push(row.path)}
                      >
                        View
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ))}
      </Grid>
    </Grid>
  );
}
